import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import CustomModal from '../../common/CustomModal';
import CustomButton from '../../../components/CustomButton';
import LabelWithHelp from '../../../components/LabelWithHelp';
import Toggle from '../../../components/ui/Toggle';
import SearchableSelectField, {
    SearchableSelectOption,
} from '../../../components/ui/SearchableSelectField';
import { useCreateCodeMutation } from '../../../api/voucher/voucherApiSlice';
import { CodeType } from '../../../api/voucher/voucherApiTypes';
import Command from '../../../action/command';
import { makePermittedIssueReportListSelector } from '../../../selector/issueReportsSelector';
import { IssueReport } from '../../../api/types';
import { buildProblemReportOptions } from './problemReportOptions';
import CodeTermsFields, {
    ALL_OBJECTS_SCOPE,
    codeTermsValid,
    endOfDayIso,
    fieldClass,
    localToday,
} from './CodeTermsFields';
import { defaultCodeScope } from './codeScope';

// Shared promo/goodwill create scaffold (codes-plan §5 Phase 2). Both code kinds
// share the same fields (code, discount, validity, uses); goodwill adds a
// customer reference + an optional source reference (the issue/booking it
// compensates) and quick 20/50/100 % presets. The kind is a prop so the two tabs
// reuse one form instead of duplicating it.
//
// Behind the contract: goodwill persistence needs the `code_type` column +
// goodwill columns from the #1000 migration and the BE goodwill-create handler
// (#1189). Until those land, the goodwill create action stays gated in
// VouchersCodesPage; promo create is live today.

// Mirrors the backend Layer-A constraint (codes-plan §9): 4–32 chars,
// letters/digits/hyphen. Case is irrelevant (redemption is case-insensitive).
const CODE_RE = /^[A-Za-z0-9-]{4,32}$/;

// The group / discount / expiry fields and their helpers live in
// CodeTermsFields, shared with the campaign form (#2249).

// Goodwill compensations are single-use for one customer — the max-uses field is
// hidden and the limit is fixed at 1 (Neuland call, 2026-07-08).
const GOODWILL_LIMIT = '1';
const GOODWILL_PRESETS = [20, 50, 100];

// Goodwill codes keep a human prefix (e.g. "KULANZ-") and append a random,
// hard-to-guess suffix so each grant is unique. Ambiguous glyphs (0/O/1/I) are
// excluded so the code stays easy to read out to a customer. The alphabet has
// exactly 32 chars (a divisor of 2^32) so the modulo below is bias-free.
const SUFFIX_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const SUFFIX_LENGTH = 6;
const randomSuffix = () => {
    const bytes = new Uint32Array(SUFFIX_LENGTH);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (n) => SUFFIX_ALPHABET[n % SUFFIX_ALPHABET.length]).join('');
};

/**
 * Snapshot of the goodwill form, carried across the detour to the
 * problem-report form and back. Plain scalars only — react-router serializes
 * navigation state.
 */
export interface GoodwillDraft {
    code: string;
    groupId: string;
    isPercentage: boolean;
    value: string;
    validUntil: string;
    maxLimit: string;
    customerReference: string;
    sourceReference: string;
    /** Why the code is issued (#3332 D4). Optional so an older draft still restores. */
    creationReason?: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    /** Which kind of code the form creates. Defaults to a promotional code. */
    codeType?: CodeType;
    /** Optional callback invoked with the created code on a successful create,
     * before the modal closes. Lets a caller auto-select the new code. */
    onCreated?: (code: string) => void;
    /** Form values to restore. Set when the user came back from filing a
     * problem report from inside this modal, so nothing they had already
     * entered is lost over the round trip. Must be a stable reference — it is
     * applied whenever its identity changes. */
    resumeDraft?: GoodwillDraft;
    /** Leave the goodwill modal to file a problem report and come back. Gets
     * the current form values so the caller can hand them back via
     * `resumeDraft`. */
    onCreateProblemReport?: (draft: GoodwillDraft) => void;
}

const CreateCodeModal = ({
    open,
    onClose,
    codeType = 'PROMOTIONAL',
    onCreated,
    resumeDraft,
    onCreateProblemReport,
}: Props) => {
    const { t, i18n } = useTranslation();
    const language: 'de' | 'en' = i18n.language === 'de' ? 'de' : 'en';
    const isGoodwill = codeType === 'GOODWILL_COMPENSATION';
    // Promo and goodwill share the field labels; only the title, messages and the
    // goodwill-only fields differ.
    const ns = isGoodwill ? 'create_goodwill' : 'create_promo';

    const [code, setCode] = useState(resumeDraft?.code ?? '');
    // #3052: promo and goodwill codes both start on "all objects".
    const defaultGroupId = defaultCodeScope();
    const [groupId, setGroupId] = useState(resumeDraft?.groupId ?? defaultGroupId);
    const [isPercentage, setIsPercentage] = useState(resumeDraft?.isPercentage ?? true);
    const [value, setValue] = useState(resumeDraft?.value ?? '');
    const [validUntil, setValidUntil] = useState(resumeDraft?.validUntil ?? '');
    const [maxLimit, setMaxLimit] = useState(
        resumeDraft?.maxLimit ?? (isGoodwill ? GOODWILL_LIMIT : ''),
    );
    const [customerReference, setCustomerReference] = useState(
        resumeDraft?.customerReference ?? '',
    );
    const [sourceReference, setSourceReference] = useState(resumeDraft?.sourceReference ?? '');
    // Why the code exists (#3332 D4): required for a promo code, which has no
    // other reason; an optional note on a goodwill code, whose reason is its
    // problem report.
    const [creationReason, setCreationReason] = useState(resumeDraft?.creationReason ?? '');
    // #2248: cap the code at one redemption per customer, on top of the global
    // usage limit above. Goodwill codes are already single-use for one named
    // customer, so the switch is only offered on promo codes.
    const [oncePerCustomer, setOncePerCustomer] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const [createCode, { isLoading }] = useCreateCodeMutation();

    const dispatch = useDispatch();
    // Problem reports for the goodwill picker — same Redux-saga source as the
    // Issue reports page. Fetched when the goodwill modal opens. A goodwill code
    // MUST link to one; the customer is then derived server-side from its order.
    const selectReports = useMemo(() => makePermittedIssueReportListSelector(), []);
    const reports = useSelector(selectReports) as IssueReport[];
    useEffect(() => {
        if (open && isGoodwill) {
            dispatch(Command.IssueReport.fetchIssueReports({ includeArchived: false }));
        }
    }, [open, isGoodwill, dispatch]);

    // Restore what the user had already filled in before they left to file a
    // problem report, with that new report now linked. Keyed on the draft's
    // identity (the caller holds it in state, so it only changes on an actual
    // resume) — re-running on every render would stomp their edits.
    useEffect(() => {
        if (!resumeDraft) return;
        setCode(resumeDraft.code);
        setGroupId(resumeDraft.groupId);
        setIsPercentage(resumeDraft.isPercentage);
        setValue(resumeDraft.value);
        setValidUntil(resumeDraft.validUntil);
        setMaxLimit(resumeDraft.maxLimit);
        setCustomerReference(resumeDraft.customerReference);
        setSourceReference(resumeDraft.sourceReference);
        setCreationReason(resumeDraft.creationReason ?? '');
    }, [resumeDraft]);

    // Newest report first: a goodwill code is nearly always granted for
    // something that just happened, and the previous list had no discernible
    // order at all. Every row carries its creation date so the ordering is
    // visible rather than implied. Built by a separately tested pure helper.
    const reportOptions = useMemo<SearchableSelectOption[]>(
        () => buildProblemReportOptions(reports, { t, language }),
        [reports, t, language],
    );

    const today = useMemo(localToday, []);

    // Goodwill code helper: take whatever the user typed as the prefix (falling
    // back to a sensible default), trim it so the suffix still fits in 32 chars,
    // and append a random tail. The user can still edit the result freely.
    const generateCode = () => {
        const rawPrefix = code.trim() || t('vouchers_codes.create_goodwill.default_prefix');
        // Strip anything the code regex would reject so the generator always
        // yields a valid code, then leave room for the suffix within 32 chars.
        const prefix = rawPrefix.replace(/[^A-Za-z0-9-]/g, '').slice(0, 32 - SUFFIX_LENGTH);
        setCode(`${prefix}${randomSuffix()}`);
    };

    const reset = () => {
        setCode('');
        setGroupId(defaultGroupId);
        setIsPercentage(true);
        setValue('');
        setValidUntil('');
        setMaxLimit(isGoodwill ? GOODWILL_LIMIT : '');
        setCustomerReference('');
        setSourceReference('');
        setCreationReason('');
        setOncePerCustomer(false);
        setFormError(null);
    };

    const close = () => {
        reset();
        onClose();
    };

    // Validation derives from form state (not API data), per the edit-form rule.
    const numValue = Number(value);
    const numLimit = Number(maxLimit);
    const isValid =
        CODE_RE.test(code.trim()) &&
        codeTermsValid({ groupId, isPercentage, value, validUntil }, today) &&
        Number.isInteger(numLimit) &&
        numLimit >= 1 &&
        (isGoodwill || creationReason.trim().length > 0) &&
        // Goodwill must always link a problem report (existing or created first) —
        // every grant stays traceable to the failure that justifies it.
        (!isGoodwill ||
            (customerReference.trim().length > 0 && sourceReference.trim().length > 0));

    const handleSubmit = async () => {
        setFormError(null);
        if (!isValid) {
            setFormError(t(`vouchers_codes.${ns}.validation`));
            return;
        }
        try {
            const created = await createCode({
                code: code.trim(),
                codeType,
                // Omitted entirely for an all-objects code — the backend stores
                // group_id/location_id NULL, which is what makes it match any
                // object of the tenant.
                ...(groupId === ALL_OBJECTS_SCOPE ? {} : { groupId }),
                discountValue: numValue,
                discountIsPercentage: isPercentage,
                validUntilDate: endOfDayIso(validUntil),
                maxLimit: numLimit,
                ...(creationReason.trim() ? { creationReason: creationReason.trim() } : {}),
                // Only meaningful for promo codes — see the switch below.
                ...(isGoodwill ? {} : { oncePerCustomer }),
                ...(isGoodwill
                    ? {
                        customerReference: customerReference.trim(),
                        sourceReference: sourceReference.trim(),
                    }
                    : {}),
            }).unwrap();
            if (created.code) onCreated?.(created.code);
            close();
        } catch (err) {
            const message =
                typeof err === 'object' &&
                err !== null &&
                'data' in err &&
                typeof (err as { data?: { message?: string } }).data?.message === 'string'
                    ? (err as { data: { message: string } }).data.message
                    : t(`vouchers_codes.${ns}.error`);
            setFormError(message);
        }
    };

    return (
        <CustomModal
            open={open}
            title={t(`vouchers_codes.${ns}.title`)}
            onClose={close}
            closeButton={
                <CustomButton title={t('common.cancel')} isFilled={false} onClick={close} />
            }
            saveButton={
                <CustomButton
                    title={t('common.save')}
                    loading={isLoading}
                    disabled={!isValid || isLoading}
                    onClick={handleSubmit}
                />
            }
        >
            <div className={'flex flex-col gap-4'}>
                {isGoodwill && (
                    <>
                        <div>
                            <LabelWithHelp
                                text={t('vouchers_codes.create_goodwill.customer_label') + ' *'}
                                help={t('vouchers_codes.create_goodwill.customer_help')}
                            />
                            <input
                                className={fieldClass}
                                value={customerReference}
                                onChange={(e) => setCustomerReference(e.target.value)}
                            />
                        </div>
                        {/* Mandatory problem-report link (#1189). Pick an existing
                            report — searchable, newest first — or file one right
                            here and come back with it preselected. */}
                        <div>
                            <LabelWithHelp
                                text={t('vouchers_codes.create_goodwill.source_label') + ' *'}
                                help={t('vouchers_codes.create_goodwill.source_help')}
                            />
                            <SearchableSelectField
                                value={sourceReference}
                                onChange={setSourceReference}
                                options={reportOptions}
                                placeholder={t('vouchers_codes.create_goodwill.source_placeholder')}
                                searchPlaceholder={t('vouchers_codes.create_goodwill.source_search_placeholder')}
                                emptyMessage={t('vouchers_codes.create_goodwill.source_empty')}
                                footer={
                                    onCreateProblemReport && (
                                        <button
                                            type={'button'}
                                            onClick={() => onCreateProblemReport({
                                                code,
                                                groupId,
                                                isPercentage,
                                                value,
                                                validUntil,
                                                maxLimit,
                                                customerReference,
                                                sourceReference,
                                                creationReason,
                                            })}
                                            className={
                                                'w-full px-3 py-2 text-left text-sm font-medium ' +
                                                'text-primary hover:bg-foreground/5'
                                            }
                                        >
                                            {t('vouchers_codes.create_goodwill.create_source')}
                                        </button>
                                    )
                                }
                            />
                            <p className={'mt-1 text-xs text-foreground/50'}>
                                {t('vouchers_codes.create_goodwill.source_hint')}
                            </p>
                        </div>
                    </>
                )}

                <div>
                    <LabelWithHelp
                        text={t('vouchers_codes.create_promo.code_label') + ' *'}
                        help={t('vouchers_codes.create_promo.help.code')}
                    />
                    <div className={isGoodwill ? 'flex gap-2' : undefined}>
                        <input
                            className={fieldClass}
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            placeholder={
                                isGoodwill
                                    ? t('vouchers_codes.create_goodwill.default_prefix')
                                    : 'SUP-W3'
                            }
                        />
                        {isGoodwill && (
                            <button
                                type={'button'}
                                onClick={generateCode}
                                className={
                                    'shrink-0 rounded-md border border-foreground/15 px-3 py-2 ' +
                                    'text-sm font-medium text-foreground/70 hover:text-foreground'
                                }
                            >
                                {t('vouchers_codes.create_goodwill.generate')}
                            </button>
                        )}
                    </div>
                    <p className={'mt-1 text-xs text-foreground/50'}>
                        {isGoodwill
                            ? t('vouchers_codes.create_goodwill.code_hint')
                            : t('vouchers_codes.create_promo.code_hint')}
                    </p>
                </div>

                <div>
                    <LabelWithHelp
                        text={isGoodwill
                            ? t('vouchers_codes.create_goodwill.reason_label')
                            : t('vouchers_codes.create_promo.reason_label') + ' *'}
                        help={isGoodwill
                            ? t('vouchers_codes.create_goodwill.reason_help')
                            : t('vouchers_codes.create_promo.reason_help')}
                    />
                    <textarea
                        className={fieldClass}
                        rows={2}
                        maxLength={500}
                        value={creationReason}
                        onChange={(e) => setCreationReason(e.target.value)}
                    />
                </div>

                <CodeTermsFields
                    open={open}
                    today={today}
                    groupId={groupId}
                    isPercentage={isPercentage}
                    value={value}
                    validUntil={validUntil}
                    onGroupIdChange={setGroupId}
                    onIsPercentageChange={setIsPercentage}
                    onValueChange={setValue}
                    onValidUntilChange={setValidUntil}
                    presets={isGoodwill ? GOODWILL_PRESETS : undefined}
                />

                {/* Goodwill codes are single-use (limit fixed at 1); only promo
                    codes expose a configurable max-uses field. */}
                {!isGoodwill && (
                    <div>
                        <LabelWithHelp
                            text={t('vouchers_codes.create_promo.max_uses_label') + ' *'}
                            help={t('vouchers_codes.create_promo.help.max_uses')}
                        />
                        <input
                            className={fieldClass}
                            type={'number'}
                            min={1}
                            step={1}
                            value={maxLimit}
                            onChange={(e) => setMaxLimit(e.target.value)}
                        />
                    </div>
                )}

                {/* #2248: a second cap, per customer, next to the global one
                    above. The description states the soft-cap caveat plainly —
                    a guest with a second e-mail address is not recognised —
                    because the admin needs that when choosing a code for a
                    campaign. Goodwill codes are already one-customer/one-use. */}
                {!isGoodwill && (
                    <Toggle
                        checked={oncePerCustomer}
                        onChange={setOncePerCustomer}
                        label={t('vouchers_codes.create_promo.once_per_customer_label')}
                        description={t('vouchers_codes.create_promo.once_per_customer_help')}
                    />
                )}

                {formError && <p className={'text-sm text-red-500'}>{formError}</p>}
            </div>
        </CustomModal>
    );
};

export default CreateCodeModal;
